/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../../ComponentLogger';
import GoogleAnalytics from '../../GoogleAnalytics';
import Observable from '../../Observable/Observable';
import ObservableEvent from './ObservableEvent';
import UpdateableComponentBase from './UpdateableComponentBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

interface IDebugSlider extends cc.Slider
{
    _dragging: boolean;
}

/** Implementation of [[UpdateableComponentBase]] to tie a [[cc.Slider]] to an observable number. */
@cc._decorator.ccclass
export default class UpdateableSliderComponent extends UpdateableComponentBase<IDebugSlider, number>
{
    @property()
    protected ignoreValueRestrictions: boolean = false;

    @property([cc.Button])
    protected DirectionalButtons: cc.Button[] = [];

    /** The [[cc.Slider]] component to be updated. */
    protected component: IDebugSlider = null;

    /** @hidden */
    // tslint:disable-next-line:no-string-literal
    private get _isSliding(): boolean { return this.component && this.component._dragging; }
    /** @hidden */
    private _wasSliding: boolean = false;

    /** @hidden */
    private _lastProgress: number = 0;

    private _hasSlidOnce: boolean = false;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.component = this.node.getComponent(cc.Slider) as IDebugSlider;

        if (!this.component)
        {
            CLog.error("No slider component. Disabled.", this, LogTypes.Initialization);
            this.enabled = false;
            return;
        }

        this.bind();
    }

    /** @hidden */
    protected update(dt: number): void
    {
        super.update(dt);

        if (!this._isSliding && this._wasSliding)
        {
            this.snapToValue();
            GoogleAnalytics.SlideEvent(this.node.name);
        }

        this._wasSliding = this._isSliding;
    }

    /** @hidden */
    protected onEnable(): void
    {
        this.component._dragging = false;
        this.onHandleReleased();
    }

    /** @hidden */
    protected onDestroy(): void
    {
        this.unbind();
        super.onDestroy();
    }

    /** @hidden */
    protected readOnlyChangedHandler = (): void =>
    {
        this.component.enabled = !this.observable.IsReadOnlyObservable.Value;
        this.component.handle.interactable = !this.observable.IsReadOnlyObservable.Value;
        this.component._dragging = false;
    }

    /** @hidden */
    protected snapToValue = (event?: cc.Event.EventCustom): void =>
    {
        if (!this.observable || !this.observable.IsValueRestricted) { return; }

        this.updateUI();
    }

    /** @hidden */
    protected updateUI = (event?: ObservableEvent<number>): void =>
    {
        if (this._isSliding)
        {
            if (event && !event.RootValueChanged)
            {
                this.stopSliding();
            }
            return;
        }

        if (!this.observable.IsValueRestricted)
        {
            let progress: number
                = (this.observable.Value - this.observable.MinValue)
                    / (this.observable.MaxValue - this.observable.MinValue);

            this.component.progress = progress;
        }
        else
        {
            // Snaps to edge case values.
            if (this._wasSliding && this._lastProgress === 0 && this._hasSlidOnce)
            {
                this.observable.Value = this.observable.MinValue;
            }
            else if (this._wasSliding && this._lastProgress === 1)
            {
                this.observable.Value = this.observable.MaxValue;
            }
            else // Snap to observable value.
            {
                this.component.progress = this.observable.ValueProgress;
            }
        }
    }

    private stopSliding(): void
    {
        this.component._dragging = false;
    }

    /**
     * When the [[cc.Slider]] is moved by user interaction, propagate the change down to the observable value.
     * @param event The dispatched slider event.
     * @param detail Ignored.
     */
    public OnBetSliderMoved(event: cc.Event.EventCustom, detail: any): void
    {
        if (!this.observable) { return; }
        event.bubbles = true;

        // tslint:disable-next-line:no-string-literal
        let progress: number = Math.floor(event["progress"] * 100) / 100 || 0;
        let direction: number = progress - this._lastProgress;

        let progressDistance: number = progress - this.observable.ValueProgress;
        let cushion: number = 0.025;

        if (this.ignoreValueRestrictions)
        {
            let distance: number = this.observable.MaxValue - this.observable.MinValue;
            distance = Math.floor(progress * distance * 100) / 100;

            let totalDistance: number = distance + this.observable.MinValue;
            this.observable.Value = totalDistance;
        }
        else if ((this._isSliding && !this._wasSliding)
            || Math.abs(progressDistance) > (this.observable.ValueStep / 2.0) + cushion
            || progress < .005
            || progress > .995)
        {
            this.setNextValue(direction, progress);
        }

        this._lastProgress = progress;
        this._hasSlidOnce = true;
    }

    private setNextValue(direction: number, progress: number): void
    {
        if (direction > 0)
        {
            this.observable.SetNextHigherValidValue(progress);
        }
        else if (direction < 0)
        {
            this.observable.SetNextLowerValidValue(progress);
        }
    }

    private bind(): void
    {
        this.onHandlePressed = (): void =>
        {
            this.DirectionalButtons.forEach(button =>
            {
                button.interactable = false;
            });
        };

        this.onHandleReleased = (): void =>
        {
            this.DirectionalButtons.forEach(button =>
            {
                button.interactable = true;
            });
        };

        // Add events to Slider Handle
        const slideButtonNode: cc.Node = this.getComponentInChildren(cc.Button).node;

        slideButtonNode.on(cc.Node.EventType.TOUCH_START, this.onHandlePressed);
        slideButtonNode.on(cc.Node.EventType.TOUCH_END, this.onHandleReleased);
        slideButtonNode.on(cc.Node.EventType.TOUCH_CANCEL, this.onHandleReleased);

        this.node.on(cc.Node.EventType.TOUCH_START, this.onHandlePressed);
        this.node.on(cc.Node.EventType.TOUCH_END, this.onHandleReleased);
        this.node.on(cc.Node.EventType.TOUCH_CANCEL, this.onHandleReleased);
    }

    private unbind(): void
    {
        const slideButtonNode: cc.Node = this.getComponentInChildren(cc.Button).node;

        slideButtonNode.off(cc.Node.EventType.TOUCH_START, this.onHandlePressed);
        slideButtonNode.off(cc.Node.EventType.TOUCH_END, this.onHandleReleased);
        slideButtonNode.off(cc.Node.EventType.TOUCH_CANCEL, this.onHandleReleased);

        this.node.off(cc.Node.EventType.TOUCH_START, this.onHandlePressed);
        this.node.off(cc.Node.EventType.TOUCH_END, this.onHandleReleased);
        this.node.off(cc.Node.EventType.TOUCH_CANCEL, this.onHandleReleased);
    }

    protected onHandlePressed = (): void => { /** PlaceHolder */ };
    protected onHandleReleased = (): void => { /** PlaceHolder */ };
}
